import { useRef, useState, type ChangeEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, FileText, Paperclip, Trash2, X } from 'lucide-react';
import {
  api,
  ApiError,
  type JobAttachment,
  type JobAttachmentContentType,
  type UploadJobAttachmentBody,
} from '../lib/api.js';
import { useAuth } from '../lib/auth-store.js';
import { blobToBase64, downscaleImage } from '../lib/image-upload.js';
import { useDialogA11y } from '../lib/use-dialog-a11y.js';
import { Button } from './ui/button.js';
import { ConfirmDialog } from './ui/confirm-dialog.js';
import { Feedback } from './ui/feedback.js';
import { QueryError } from './ui/query-error.js';

/** Server cap (MAX_ATTACHMENT_BYTES in the API). */
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPTED: Record<string, JobAttachmentContentType> = {
  'image/png': 'image/png',
  'image/jpeg': 'image/jpeg',
  'image/webp': 'image/webp',
  'image/heic': 'image/heic',
  'application/pdf': 'application/pdf',
};

export function jobFilesQueryKey(spireId: number) {
  return ['sales-order', spireId, 'attachments'] as const;
}

/**
 * Files attached to a job — photos + documents held in Tinsmith's object
 * store. Shared by the office order page (upload + delete) and the tech's
 * job screens (view, optionally upload). Photos captured through the
 * field camera flow (POST /photos) land in the same list.
 */
export function JobFiles({
  spireId,
  canUpload = true,
  emptyHint = 'No files on this job yet.',
}: {
  spireId: number;
  canUpload?: boolean;
  emptyHint?: string;
}) {
  const queryClient = useQueryClient();
  const role = useAuth((s) => s.user?.role);
  const canDelete = role !== undefined && role !== 'field_tech';
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [caption, setCaption] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);
  // An <img> that fails is usually an aged-out signed URL, so refetch for
  // fresh ones — but only once per list, or a genuinely unloadable image
  // (CSP, deleted object) would refetch in a loop.
  const retriedForRef = useRef<number | null>(null);

  const filesQuery = useQuery({
    queryKey: jobFilesQueryKey(spireId),
    queryFn: ({ signal }) => api.attachments.list(spireId, signal),
    // Read URLs are signed for ~15 min; stay comfortably inside that.
    staleTime: 5 * 60_000,
    refetchInterval: 10 * 60_000,
  });

  const upload = useMutation<JobAttachment, ApiError, UploadJobAttachmentBody>({
    mutationFn: (body) => api.attachments.upload(spireId, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: jobFilesQueryKey(spireId) }),
  });

  const onFilesPicked = async (e: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    if (picked.length === 0) return;
    setError(null);
    try {
      for (const file of picked) {
        const body = await toUploadBody(file, caption.trim());
        await upload.mutateAsync(body);
      }
      setCaption('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const files = filesQuery.data ?? [];
  const images = files.filter((f) => f.contentType.startsWith('image/'));
  const documents = files.filter((f) => !f.contentType.startsWith('image/'));
  const viewingIndex = images.findIndex((f) => f.id === viewingId);

  return (
    <div className="space-y-3">
      {canUpload && (
        <div className="space-y-2">
          <input
            aria-label="Caption (optional)"
            placeholder="Caption (optional)"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            className="block w-full h-10 rounded-lg border px-3 text-sm bg-surface text-ink"
            style={{ borderColor: 'var(--color-border-tertiary)' }}
            maxLength={500}
          />
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/png,image/jpeg,image/webp,image/heic,application/pdf"
            onChange={(e) => void onFilesPicked(e)}
            className="sr-only"
            tabIndex={-1}
          />
          <Button
            variant="secondary"
            size="sm"
            disabled={upload.isPending}
            onClick={() => fileInputRef.current?.click()}
          >
            <Paperclip className="h-4 w-4" aria-hidden />
            {upload.isPending ? 'Uploading…' : 'Add photos or PDFs'}
          </Button>
          {error && <Feedback variant="error" size="sm">{error}</Feedback>}
        </div>
      )}

      {filesQuery.isPending ? (
        <div className="text-sm text-ink-muted">Loading files…</div>
      ) : filesQuery.isError ? (
        <QueryError
          title="Couldn't load job files"
          error={filesQuery.error}
          onRetry={() => void filesQuery.refetch()}
          size="md"
        />
      ) : files.length === 0 ? (
        <div className="text-sm text-ink-muted">{emptyHint}</div>
      ) : (
        <>
          {images.length > 0 && (
            <ul className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {images.map((f) => (
                <li key={f.id}>
                  <button
                    type="button"
                    onClick={() => setViewingId(f.id)}
                    className="block w-full aspect-square overflow-hidden rounded-lg bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
                    aria-label={`View ${f.caption || f.filename}`}
                  >
                    <img
                      src={f.url}
                      alt={f.caption || f.filename}
                      loading="lazy"
                      className="h-full w-full object-cover"
                      onError={() => {
                        if (retriedForRef.current === filesQuery.dataUpdatedAt) return;
                        void filesQuery.refetch().then((r) => {
                          retriedForRef.current = r.dataUpdatedAt;
                        });
                      }}
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {documents.length > 0 && (
            <ul className="space-y-1">
              {documents.map((f) => (
                <DocumentRow key={f.id} file={f} canDelete={canDelete} spireId={spireId} />
              ))}
            </ul>
          )}
        </>
      )}

      {viewingIndex >= 0 && (
        <Lightbox
          images={images}
          index={viewingIndex}
          spireId={spireId}
          canDelete={canDelete}
          onNavigate={(i) => setViewingId(images[i]?.id ?? null)}
          onClose={() => setViewingId(null)}
        />
      )}
    </div>
  );
}

function useDeleteFile(spireId: number, onDone?: () => void) {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => api.attachments.remove(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: jobFilesQueryKey(spireId) });
      onDone?.();
    },
  });
}

function DocumentRow({
  file,
  canDelete,
  spireId,
}: {
  file: JobAttachment;
  canDelete: boolean;
  spireId: number;
}) {
  const [confirming, setConfirming] = useState(false);
  const remove = useDeleteFile(spireId, () => setConfirming(false));
  return (
    <li className="flex items-center gap-2 text-sm">
      <a
        href={file.url}
        target="_blank"
        rel="noreferrer"
        className="flex min-w-0 flex-1 items-center gap-3 rounded-lg px-2 py-2 hover:bg-surface-muted"
      >
        <FileText className="h-5 w-5 shrink-0 text-ink-subtle" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-ink">{file.caption || file.filename}</span>
          <span className="block truncate text-xs text-ink-muted">{fileMeta(file)}</span>
        </span>
      </a>
      {canDelete && (
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Remove ${file.filename}`}
          onClick={() => setConfirming(true)}
        >
          <Trash2 className="h-4 w-4" aria-hidden />
        </Button>
      )}
      {confirming && (
        <ConfirmDialog
          title="Remove file?"
          message={`${file.filename} will be removed from this job.`}
          confirmLabel="Remove"
          pending={remove.isPending}
          onConfirm={() => remove.mutate(file.id)}
          onCancel={() => setConfirming(false)}
        />
      )}
    </li>
  );
}

function Lightbox({
  images,
  index,
  spireId,
  canDelete,
  onNavigate,
  onClose,
}: {
  images: JobAttachment[];
  index: number;
  spireId: number;
  canDelete: boolean;
  onNavigate: (index: number) => void;
  onClose: () => void;
}) {
  const ref = useDialogA11y<HTMLDivElement>(onClose);
  const [confirming, setConfirming] = useState(false);
  const remove = useDeleteFile(spireId, onClose);
  const file = images[index];
  if (!file) return null;
  const hasPrev = index > 0;
  const hasNext = index < images.length - 1;

  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-label={file.caption || file.filename}
      className="fixed inset-0 z-50 flex flex-col bg-black/90 text-white"
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft' && hasPrev) onNavigate(index - 1);
        if (e.key === 'ArrowRight' && hasNext) onNavigate(index + 1);
      }}
    >
      <div className="flex items-center gap-2 p-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{file.caption || file.filename}</div>
          <div className="truncate text-xs text-white/70">{fileMeta(file)}</div>
        </div>
        {canDelete && (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="h-11 w-11 inline-flex items-center justify-center rounded-lg hover:bg-white/10"
            aria-label="Remove photo"
          >
            <Trash2 className="h-5 w-5" aria-hidden />
          </button>
        )}
        <button
          type="button"
          data-autofocus
          onClick={onClose}
          className="h-11 w-11 inline-flex items-center justify-center rounded-lg hover:bg-white/10"
          aria-label="Close"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-2 pb-4">
        <img src={file.url} alt={file.caption || file.filename} className="max-h-full max-w-full object-contain" />
        {hasPrev && (
          <button
            type="button"
            onClick={() => onNavigate(index - 1)}
            className="absolute left-2 h-11 w-11 inline-flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70"
            aria-label="Previous photo"
          >
            <ChevronLeft className="h-6 w-6" aria-hidden />
          </button>
        )}
        {hasNext && (
          <button
            type="button"
            onClick={() => onNavigate(index + 1)}
            className="absolute right-2 h-11 w-11 inline-flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70"
            aria-label="Next photo"
          >
            <ChevronRight className="h-6 w-6" aria-hidden />
          </button>
        )}
      </div>

      {confirming && (
        <ConfirmDialog
          title="Remove photo?"
          message="The photo will be removed from this job in Tinsmith. A copy already sent to Spire stays there."
          confirmLabel="Remove"
          pending={remove.isPending}
          onConfirm={() => remove.mutate(file.id)}
          onCancel={() => setConfirming(false)}
        />
      )}
    </div>
  );
}

function fileMeta(file: JobAttachment): string {
  const when = new Date(file.createdAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
  const size =
    file.sizeBytes >= 1024 * 1024
      ? `${(file.sizeBytes / (1024 * 1024)).toFixed(1)} MB`
      : `${Math.max(1, Math.round(file.sizeBytes / 1024))} KB`;
  return [when, file.uploadedByName, size].filter(Boolean).join(' · ');
}

async function toUploadBody(file: File, caption: string): Promise<UploadJobAttachmentBody> {
  const contentType = ACCEPTED[file.type] ?? contentTypeFromName(file.name);
  if (!contentType) throw new Error(`${file.name}: only photos (PNG, JPEG, WebP, HEIC) and PDFs can be attached.`);

  let blob: Blob = file;
  let outType = contentType;
  let filename = file.name;
  if (contentType === 'image/jpeg' || contentType === 'image/png' || contentType === 'image/webp') {
    const shrunk = await downscaleImage(file);
    if (shrunk) {
      blob = shrunk;
      outType = 'image/jpeg';
      filename = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    }
  }
  if (blob.size > MAX_BYTES) throw new Error(`${file.name} is over the 10 MB limit.`);

  return {
    contentType: outType,
    base64: await blobToBase64(blob),
    filename,
    ...(caption ? { caption } : {}),
  };
}

function contentTypeFromName(name: string): JobAttachmentContentType | undefined {
  const ext = name.toLowerCase().split('.').pop();
  switch (ext) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'webp':
      return 'image/webp';
    case 'heic':
      return 'image/heic';
    case 'pdf':
      return 'application/pdf';
    default:
      return undefined;
  }
}
